import { useEffect, useMemo, useState } from "react";

export default function CompletionProgress({ token, user }) {
  const initialLevel = useMemo(() => {
    if (user.role_id === 1) return 2; // state -> districts
    if (user.role_id === 2) return 3; // district -> divisions
    return 4; // division -> sectors
  }, [user.role_id]);

  const defaultSortForLevel = lvl => (
    lvl === 4
      ? { field: "member_count", direction: "desc" }
      : { field: "completed_sectors", direction: "desc" }
  );

  const [level, setLevel] = useState(initialLevel);
  const [districtFilter, setDistrictFilter] = useState(
    user.role_id === 2 ? { id: user.id, name: user.orgname } : null
  );
  const [divisionFilter, setDivisionFilter] = useState(
    user.role_id === 3 ? { id: user.id, name: user.orgname, districtName: null } : null
  );
  const [data, setData] = useState({ items: [], limit: 33 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const initialSort = defaultSortForLevel(initialLevel);
  const [sortField, setSortField] = useState(initialSort.field);
  const [sortDir, setSortDir] = useState(initialSort.direction);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      setLoading(true);
      setError("");
      try {
        const params = new URLSearchParams({ level: String(level) });
        if (districtFilter?.id) params.set("district_id", String(districtFilter.id));
        if (divisionFilter?.id) params.set("division_id", String(divisionFilter.id));

        const res = await fetch(`https://smartcoreapi.ssfkerala.org/analytics/completions.php?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal
        });

        if (!res.ok) {
          const message = await res.text();
          throw new Error(message || "Failed to load completion data");
        }

        const json = await res.json();
        setData(json);
      } catch (err) {
        if (err.name !== "AbortError") {
          setError(err.message || "Failed to load completion data");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => controller.abort();
  }, [level, districtFilter?.id, divisionFilter?.id, token]);

  useEffect(() => {
    const defaults = defaultSortForLevel(level);
    setSortField(defaults.field);
    setSortDir(defaults.direction);
  }, [level]);

  const headers = useMemo(() => {
    if (level === 4) {
      return [
        { key: "name", label: "Sector" },
        { key: "division_name", label: "Division" },
        { key: "district_name", label: "District" },
        { key: "member_count", label: "Members" },
        { key: "completed", label: "Status" }
      ];
    }
    if (level === 3) {
      return [
        { key: "name", label: "Division" },
        { key: "district_name", label: "District" },
        { key: "completed_sectors", label: "Completed" },
        { key: "remaining", label: "Remaining" },
        { key: "total_sectors", label: "Total Sectors" }
      ];
    }
    return [
      { key: "name", label: "District" },
      { key: "completed_sectors", label: "Completed" },
      { key: "remaining", label: "Remaining" },
      { key: "total_sectors", label: "Total Sectors" }
    ];
  }, [level]);

  const breadcrumbs = useMemo(() => {
    const items = [{ label: "Overview", onClick: () => resetToRoot() }];
    if (districtFilter) {
      items.push({ label: districtFilter.name, onClick: () => handleBackToDistrict() });
    }
    if (divisionFilter && level === 4) {
      items.push({ label: divisionFilter.name, onClick: () => handleBackToDivision() });
    }
    return items;
  }, [districtFilter, divisionFilter, level]);

  const resetToRoot = () => {
    if (user.role_id === 1) {
      setDistrictFilter(null);
      setDivisionFilter(null);
      setLevel(2);
    }
  };

  const handleBackToDivision = () => {
    if (user.role_id <= 3) {
      setDivisionFilter(null);
      setLevel(3);
    }
  };

  const handleBackToDistrict = () => {
    if (user.role_id === 1) {
      setDistrictFilter(null);
      setLevel(2);
    }
  };

  const handleRowClick = item => {
    if (level === 2) {
      setDistrictFilter({ id: item.entity_id, name: item.name });
      setDivisionFilter(null);
      setLevel(3);
    } else if (level === 3) {
      setDivisionFilter({ id: item.entity_id, name: item.name, districtName: item.district_name });
      setLevel(4);
    }
  };

  const canClickRows = useMemo(() => {
    if (level === 2) return user.role_id <= 2; // state only
    if (level === 3) return true; // state & district
    return false;
  }, [level, user.role_id]);

  const sortAccessors = useMemo(() => {
    if (level === 4) {
      return {
        name: item => item.name ?? "",
        division_name: item => item.division_name ?? "",
        district_name: item => item.district_name ?? "",
        member_count: item => item.member_count ?? 0,
        completed: item => (item.completed ? 1 : 0)
      };
    }
    if (level === 3) {
      return {
        name: item => item.name ?? "",
        district_name: item => item.district_name ?? "",
        completed_sectors: item => item.completed_sectors ?? 0,
        remaining: item => Math.max((item.total_sectors ?? 0) - (item.completed_sectors ?? 0), 0),
        total_sectors: item => item.total_sectors ?? 0
      };
    }
    return {
      name: item => item.name ?? "",
      completed_sectors: item => item.completed_sectors ?? 0,
      remaining: item => Math.max((item.total_sectors ?? 0) - (item.completed_sectors ?? 0), 0),
      total_sectors: item => item.total_sectors ?? 0
    };
  }, [level]);

  const sortedItems = useMemo(() => {
    if (!data.items || data.items.length === 0) return [];

    const itemsCopy = [...data.items];
    const accessor = sortAccessors[sortField] ?? (item => item.name ?? "");

    const compare = (a, b) => {
      const valA = accessor(a);
      const valB = accessor(b);

      if (typeof valA === "number" && typeof valB === "number") {
        return valA - valB;
      }

      const strA = String(valA ?? "").toLowerCase();
      const strB = String(valB ?? "").toLowerCase();
      if (strA < strB) return -1;
      if (strA > strB) return 1;
      return 0;
    };

    itemsCopy.sort((a, b) => {
      const result = compare(a, b);
      return sortDir === "asc" ? result : -result;
    });

    return itemsCopy;
  }, [data.items, sortAccessors, sortDir, sortField]);

  const hasData = sortedItems.length > 0;

  const handleSort = key => {
    if (!sortAccessors[key]) return;
    if (sortField === key) {
      setSortDir(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(key);
      setSortDir(key === "name" || key === "division_name" || key === "district_name" ? "asc" : "desc");
    }
  };

  const hierarchyLabel = useMemo(() => {
    const parts = [];
    if (user.role_id === 1) {
      parts.push("State");
    }
    if (districtFilter?.name) {
      parts.push(districtFilter.name);
    }
    if (divisionFilter?.name) {
      parts.push(divisionFilter.name);
    }
    if (parts.length === 0) {
      parts.push(user.orgname || "Overview");
    }
    return parts.join(" → ");
  }, [user.role_id, user.orgname, districtFilter, divisionFilter]);

  const levelLabel = useMemo(() => {
    if (level === 2) return "District Overview";
    if (level === 3) return "Division Overview";
    return "Sector Details";
  }, [level]);

  const buildExportData = () => {
    if (!hasData) return null;

    if (level === 2) {
      const headers = ["District", "Completed Sectors", "Remaining Sectors", "Total Sectors"];
      const rows = sortedItems.map(item => {
        const total = item.total_sectors ?? 0;
        const completed = item.completed_sectors ?? 0;
        const remaining = Math.max(total - completed, 0);
        return [item.name ?? "", completed, remaining, total];
      });
      return { headers, rows };
    }

    if (level === 3) {
      const headers = ["Division", "District", "Completed Sectors", "Remaining Sectors", "Total Sectors"];
      const rows = sortedItems.map(item => {
        const total = item.total_sectors ?? 0;
        const completed = item.completed_sectors ?? 0;
        const remaining = Math.max(total - completed, 0);
        return [item.name ?? "", item.district_name ?? "", completed, remaining, total];
      });
      return { headers, rows };
    }

    const headers = ["Sector", "Division", "District", "Members", "Status"];
    const rows = sortedItems.map(item => {
      const limitValue = data.limit ?? 33;
      const pending = Math.max(limitValue - (item.member_count ?? 0), 0);
      const status = item.completed ? "Completed" : `Pending (${pending})`;
      return [
        item.name ?? "",
        item.division_name ?? "",
        item.district_name ?? "",
        item.member_count ?? 0,
        status
      ];
    });
    return { headers, rows };
  };

  const handleCSVExport = () => {
    const exportData = buildExportData();
    if (!exportData || exportData.rows.length === 0) {
      alert("No data to export");
      return;
    }

    const csvRows = [exportData.headers.join(",")];
    exportData.rows.forEach(row => {
      csvRows.push(row.map(value => {
        const str = String(value ?? "");
        return `"${str.replace(/"/g, '""')}"`;
      }).join(","));
    });

    const blob = new Blob([csvRows.join("\n")], { type: "text/csv" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `completion_progress_level_${level}.csv`;
    link.click();
  };

  const handleWhatsAppShare = () => {
    if (!hasData) {
      alert("No data to share");
      return;
    }

    const now = new Date();
    const limitValue = data.limit ?? 33;
    const timeString = now.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true
    });

    const lines = sortedItems.map((item, index) => {
      const position = `${index + 1}.`;
      if (level === 2) {
        const total = item.total_sectors ?? 0;
        const completed = item.completed_sectors ?? 0;
        const remaining = Math.max(total - completed, 0);
        return `${position} ${item.name ?? "Unknown"} — Completed ${completed}/${total}, Remaining ${remaining}`;
      }

      if (level === 3) {
        const total = item.total_sectors ?? 0;
        const completed = item.completed_sectors ?? 0;
        const remaining = Math.max(total - completed, 0);
        return `${position} ${item.name ?? "Unknown"} (${item.district_name ?? "District"}) — Completed ${completed}/${total}, Remaining ${remaining}`;
      }

      const pending = Math.max(limitValue - (item.member_count ?? 0), 0);
      const status = item.completed ? "Completed" : `Pending (need ${pending})`;
      return `${position} ${item.name ?? "Unknown"} — ${item.member_count ?? 0} members, ${status} [${item.division_name ?? "Division"} • ${item.district_name ?? "District"}]`;
    });

    const header = `Future Summit Smart Core\nCompletion Progress — ${levelLabel}\n${hierarchyLabel}\nGenerated: ${timeString}\nEntries: ${sortedItems.length}\nThreshold: ${limitValue} members`;
    const message = `${header}\n\n${lines.join("\n")}`;
    const url = `https://wa.me/?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  const renderTableHeader = () => {
    return (
      <tr>
        {headers.map(column => (
          <th
            key={column.key}
            onClick={() => handleSort(column.key)}
            className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider cursor-pointer select-none"
          >
            <span className="inline-flex items-center gap-1">
              {column.label}
              {sortField === column.key && (
                <span>{sortDir === "asc" ? "▲" : "▼"}</span>
              )}
            </span>
          </th>
        ))}
      </tr>
    );
  };

  const renderTableBody = () => {
    if (!hasData) {
      return (
        <tr>
          <td colSpan={headers.length} className="px-4 py-6 text-center text-gray-500">
            No data available
          </td>
        </tr>
      );
    }

    if (level === 4) {
      return sortedItems.map(item => {
        const limitValue = data.limit ?? 33;
        const pendingNeeded = Math.max(limitValue - (item.member_count ?? 0), 0);

        return (
          <tr key={item.entity_id} className="hover:bg-gray-50 transition">
            <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
            <td className="px-4 py-3 text-sm text-gray-600">{item.division_name}</td>
            <td className="px-4 py-3 text-sm text-gray-600">{item.district_name}</td>
            <td className="px-4 py-3 text-sm text-gray-600">{item.member_count}</td>
            <td className="px-4 py-3 text-sm">
              <span className={`inline-flex px-3 py-1 rounded-lg text-xs font-semibold ${item.completed ? "bg-green-100 text-green-700" : "bg-yellow-100 text-yellow-700"}`}>
                {item.completed ? "Completed" : `Pending (${pendingNeeded})`}
              </span>
            </td>
          </tr>
        );
      });
    }

    return sortedItems.map(item => {
      const total = item.total_sectors || 0;
      const completed = item.completed_sectors || 0;
      const remaining = Math.max(total - completed, 0);

      const clickable = canClickRows;

      return (
        <tr
          key={item.entity_id}
          className={clickable ? "hover:bg-gray-50 transition cursor-pointer" : "hover:bg-gray-50 transition"}
          onClick={() => clickable && handleRowClick(item)}
        >
          <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.name}</td>
          {level === 3 && <td className="px-4 py-3 text-sm text-gray-600">{item.district_name}</td>}
          <td className="px-4 py-3 text-sm text-gray-600">{completed}</td>
          <td className="px-4 py-3 text-sm text-gray-600">{remaining}</td>
          <td className="px-4 py-3 text-sm text-gray-600">{total}</td>
        </tr>
      );
    });
  };

  const renderBreadcrumbs = () => (
    <nav className="flex items-center text-sm text-gray-600 gap-2">
      {breadcrumbs.map((crumb, index) => (
        <span key={crumb.label} className="flex items-center gap-2">
          {index > 0 && <span className="text-gray-400">/</span>}
          <button
            type="button"
            onClick={crumb.onClick}
            className="hover:text-blue-600 focus:outline-none"
            disabled={index === breadcrumbs.length - 1}
          >
            {crumb.label}
          </button>
        </span>
      ))}
    </nav>
  );

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Completion Progress</h2>
        <div className="text-sm text-gray-500">Threshold: {data.limit ?? 33} members per sector</div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-4">
          {renderBreadcrumbs()}
          <div className="text-sm text-gray-600">
            {level === 2 && "Select a district to view its divisions."}
            {level === 3 && "Select a division to view its sectors."}
            {level === 4 && "Sectors reaching the member target are marked as completed."}
          </div>
        </div>
        {hasData && (
          <div className="flex flex-wrap items-center justify-end gap-2">
            <button
              type="button"
              onClick={handleCSVExport}
              className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition"
            >
              Export CSV
            </button>
            <button
              type="button"
              onClick={handleWhatsAppShare}
              className="px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 transition"
            >
              Share to WhatsApp
            </button>
          </div>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
          {error}
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="inline-block w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">{renderTableHeader()}</thead>
              <tbody className="divide-y divide-gray-200">{renderTableBody()}</tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
